// Salary data by role and city for In-House vs Agency Calculator
export const SALARY_DATA = {
  roles: {
    creativeDirector: { title: 'Creative Director', base: 165000, description: 'Leads creative vision and brand direction' },
    brandStrategist: { title: 'Brand Strategist', base: 115000, description: 'Develops brand positioning and messaging' },
    uiuxDesigner: { title: 'UI/UX Designer', base: 105000, description: 'Designs user interfaces and experiences' },
    graphicDesigner: { title: 'Graphic Designer', base: 75000, description: 'Creates visual assets and marketing materials' },
    webflowDeveloper: { title: 'Webflow Developer', base: 95000, description: 'Builds and maintains Webflow sites' },
    frontendDeveloper: { title: 'Frontend Developer', base: 115000, description: 'Custom code and integrations' },
    projectManager: { title: 'Project Manager', base: 95000, description: 'Coordinates projects and stakeholders' },
    contentStrategist: { title: 'Content Strategist', base: 85000, description: 'Plans and oversees content creation' },
    seoSpecialist: { title: 'SEO Specialist', base: 75000, description: 'Optimizes search visibility' },
    paidMediaManager: { title: 'Paid Media Manager', base: 85000, description: 'Manages advertising campaigns' },
  },
  cities: {
    sanFrancisco: { name: 'San Francisco', multiplier: 1.35 },
    newYork: { name: 'New York City', multiplier: 1.25 },
    losAngeles: { name: 'Los Angeles', multiplier: 1.15 },
    seattle: { name: 'Seattle', multiplier: 1.20 },
    austin: { name: 'Austin', multiplier: 1.05 },
    denver: { name: 'Denver', multiplier: 1.00 },
    chicago: { name: 'Chicago', multiplier: 1.05 },
    atlanta: { name: 'Atlanta', multiplier: 0.95 },
    remote: { name: 'Remote (US Average)', multiplier: 1.00 },
  },
  // Additional hiring costs
  recruitingCostPercent: 0.20, // 20% of first-year salary
  rampUpMonths: 3, // Average months to full productivity
  turnoverRate: 0.23, // Annual turnover rate for marketing/creative roles
  benefitsMultiplier: 0.30, // Default benefits overhead (30%)
};

// Helper to get personalized FAQs based on company size, CMS, and industry
export function getFAQs(companySize, currentCMS, industry, cmsData, industryData) {
  const cmsName = cmsData?.name || 'your current CMS';
  const cmsCategory = cmsData?.category || 'CMS';
  const industryName = industryData?.name || 'your industry';

  // Base FAQs that apply to everyone
  const baseFAQs = [
    {
      q: `How long does a ${cmsName} to Webflow migration take?`,
      a: companySize === 'startup'
        ? `For ${cmsCategory} platforms like ${cmsName}, most migrations take 6-8 weeks. This includes design, development, content migration, and training your team on Webflow.`
        : companySize === 'midmarket'
        ? `Migrating from ${cmsName} typically takes 8-12 weeks for mid-market companies. We handle discovery, design, development, content migration, SEO preservation, and comprehensive team training.`
        : `Enterprise migrations from ${cmsName} take 10-14 weeks, accounting for complex integrations, compliance requirements, and stakeholder coordination. We've completed 50+ enterprise ${cmsCategory} migrations.`
    },
    {
      q: 'Will we lose our SEO rankings during migration?',
      a: industry === 'ecommerce'
        ? `No. E-commerce SEO is critical, and we implement comprehensive migration strategies: 301 redirects for all product pages, metadata preservation, schema markup migration, and sitemap resubmission. Most e-commerce clients see improved rankings within 30 days.`
        : industry === 'media'
        ? `No. For content-heavy ${industryName} sites, we're especially careful with SEO. We preserve all URLs where possible, implement comprehensive redirects, migrate all metadata, and ensure your content archive remains fully indexed.`
        : `No. We implement comprehensive SEO migration strategies including 301 redirects, metadata preservation, sitemap submission, and structured data migration. Most clients see improved rankings within 30 days of launch.`
    },
  ];

  // Company size specific FAQs
  const sizeFAQs = {
    startup: [
      {
        q: 'Is Azai the right fit for a growing company?',
        a: `We primarily work with enterprise clients, but we selectively partner with ambitious growing companies. For hands-on enterprise migration support, companies typically invest $25K+ with Azai Studios. We also offer a free migration toolkit and self-service resources to help you get started on your own.`
      },
      {
        q: 'What self-service resources do you offer?',
        a: `We provide a free migration toolkit including step-by-step migration guides, Webflow templates, and a resource library. When you're ready for enterprise-grade support, you can upgrade to a full-service engagement at any time.`
      },
    ],
    midmarket: [
      {
        q: `What makes Webflow better than ${cmsName} for mid-market companies?`,
        a: `${cmsName} often requires dedicated developers for even simple changes. Webflow lets your marketing team update content, launch landing pages, and manage the blog without engineering tickets. Most mid-market clients save 15-20 developer hours per month. Typical project investment is $25K - $75K.`
      },
      {
        q: 'What\'s included in the Agency of Record retainer?',
        a: `Everything for ongoing website and brand success: unlimited design requests, Webflow updates and new pages, graphic design for campaigns, brand strategy guidance, and paid media planning. It's like having a full creative team on-call for a fraction of hiring in-house.`
      },
    ],
    enterprise: [
      {
        q: `How do you handle enterprise ${cmsCategory} migrations with complex integrations?`,
        a: `We've migrated 50+ enterprise sites from platforms like ${cmsName}. We audit all integrations upfront, work with your IT team on API connections, and build custom solutions where needed. Our process includes dedicated QA, staging environments, and executive-level project oversight.`
      },
      {
        q: 'What about enterprise security and compliance requirements?',
        a: industry === 'healthcare'
          ? `Webflow Enterprise is SOC 2 compliant with HIPAA-ready infrastructure. We've built healthcare sites that passed rigorous compliance audits. We work directly with your security and legal teams throughout the process. Every enterprise engagement includes dedicated project management and zero-downtime migration guarantee.`
          : industry === 'fintech'
          ? `Webflow Enterprise offers SOC 2 compliance, SSO/SAML, and enterprise-grade security. We've built financial services sites that met strict regulatory requirements. Your compliance team will be involved at every stage.`
          : `Webflow Enterprise is SOC 2 compliant with SSL, DDoS protection, and 99.99% uptime SLA. We work directly with your security team and can accommodate custom compliance requirements. All enterprise engagements include dedicated PM, QA, and post-launch support.`
      },
      {
        q: 'What does the enterprise migration process look like?',
        a: `Our enterprise process includes: (1) Discovery & audit of your current ${cmsName} setup, (2) Strategy & architecture planning with your stakeholders, (3) Design in Webflow with dedicated creative team, (4) Development with enterprise SLAs, (5) Zero-downtime migration with full SEO preservation, (6) Training, QA, and post-launch support. Typical enterprise timeline is 10-14 weeks.`
      },
    ],
    largeEnterprise: [
      {
        q: 'Can you handle multi-site enterprise migrations?',
        a: `Yes. We've consolidated multi-site environments onto Webflow Enterprise. This typically involves auditing all existing properties, creating a unified design system, and rolling out in phases. We can manage global sites across multiple regions and languages. Each engagement includes a dedicated project team with enterprise SLAs.`
      },
      {
        q: 'How do you work with our existing agency and internal teams?',
        a: `We integrate seamlessly with your existing ecosystem. We can work alongside your media agency, collaborate with internal creative teams, and coordinate with IT on integrations. Many Fortune 500 clients use us as their dedicated Webflow partner while maintaining other agency relationships. We provide executive-level project oversight and regular stakeholder reporting.`
      },
    ],
  };

  // Industry specific FAQs
  const industryFAQs = {
    ecommerce: [
      {
        q: 'Can Webflow handle our e-commerce requirements?',
        a: `Webflow's native e-commerce works great for catalog sites and smaller stores. For larger operations, we typically build Webflow as the marketing frontend while integrating with Shopify, BigCommerce, or your existing commerce platform for transactions.`
      },
    ],
    healthcare: [
      {
        q: 'How do you ensure HIPAA compliance?',
        a: `While Webflow itself doesn't store PHI, we architect solutions that keep patient data in compliant systems (like your EHR or patient portal) while using Webflow for marketing content. All forms can be configured to route to HIPAA-compliant endpoints.`
      },
    ],
    fintech: [
      {
        q: 'How do you handle financial services compliance requirements?',
        a: `We work with your compliance team from day one. All copy goes through your legal review, we implement required disclosures, and we build approval workflows into the CMS. We've launched sites for banks, investment firms, and fintech startups.`
      },
    ],
    saas: [
      {
        q: 'Can Webflow integrate with our product and documentation?',
        a: `Yes. We can embed your app, integrate with documentation platforms like GitBook or ReadMe, and build custom product pages that pull data from your APIs. Many SaaS clients use Webflow for marketing while keeping docs and app separate.`
      },
    ],
  };

  // General FAQs
  const generalFAQs = [
    {
      q: 'Can our marketing team make updates without developers?',
      a: `Absolutely. That's one of Webflow's biggest advantages over ${cmsName}. We train your team on the Editor, so they can update text, images, blog posts, and CMS content without touching code or waiting on developers. Enterprise teams typically reclaim 15-20 developer hours per month.`
    },
    {
      q: 'What happens if we need to cancel the AOR engagement?',
      a: companySize === 'startup'
        ? `There's no long-term lock-in. After an initial 3-month commitment, our AOR is month-to-month. You own your Webflow site and can self-manage anytime.`
        : `There's no long-term lock-in. Our AOR is month-to-month after the initial 3-month commitment. We earn your business every month by delivering results. You own everything we create — and our enterprise clients stay with us for years because we continuously drive results.`
    },
  ];

  // Combine FAQs based on selections
  const selectedSizeFAQs = sizeFAQs[companySize] || sizeFAQs.enterprise;
  const selectedIndustryFAQs = industryFAQs[industry] || [];

  return [
    ...baseFAQs,
    ...selectedSizeFAQs,
    ...selectedIndustryFAQs,
    ...generalFAQs,
  ].slice(0, 6); // Limit to 6 FAQs
}
